/**
 * Phase 9: LazyImage Component
 * Uses Intersection Observer for efficient lazy loading of images
 * Improves performance by only loading images when they're about to enter the viewport
 */

import React, { useState, useRef, useEffect } from "react";
import "./LazyImage.module.css";

// Default placeholder SVG (lightweight gray placeholder with a camera icon)
const DEFAULT_PLACEHOLDER =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 200'%3E%3Crect fill='%23f1f5f9' width='300' height='200'/%3E%3Cpath fill='%23cbd5e1' d='M150 70c-16.5 0-30 13.5-30 30s13.5 30 30 30 30-13.5 30-30-13.5-30-30-30zm0 50c-11 0-20-9-20-20s9-20 20-20 20 9 20 20-9 20-20 20z'/%3E%3Cpath fill='%23cbd5e1' d='M180 60h-15l-5-10h-20l-5 10h-15c-5.5 0-10 4.5-10 10v60c0 5.5 4.5 10 10 10h60c5.5 0 10-4.5 10-10V70c0-5.5-4.5-10-10-10zm0 70H120V70h60v60z'/%3E%3C/svg%3E";

/**
 * LazyImage Component
 * @param {string} src - The image source URL
 * @param {string} alt - Alt text for the image
 * @param {string} placeholder - Custom placeholder image (optional)
 * @param {string} className - Additional CSS classes
 * @param {Object} style - Inline styles
 * @param {number} rootMargin - Margin around the root for early loading (default: 50px)
 * @param {number} threshold - Visibility threshold to trigger loading (0-1, default: 0.1)
 * @param {function} onLoad - Callback when image loads
 * @param {function} onError - Callback when image fails to load
 * @param {boolean} fadeIn - Whether to fade in the image (default: true)
 * @param {string} objectFit - CSS object-fit property (default: 'cover')
 * @param {Object} imgProps - Additional props to pass to the img element
 */
const LazyImage = ({
  src,
  alt = "",
  placeholder = DEFAULT_PLACEHOLDER,
  className = "",
  style = {},
  rootMargin = "50px",
  threshold = 0.1,
  onLoad,
  onError,
  fadeIn = true,
  objectFit = "contain",
  ...imgProps
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    // Reset state when src changes
    setIsLoaded(false);
    setHasError(false);
    setIsInView(false);
  }, [src]);

  useEffect(() => {
    // Check if IntersectionObserver is supported
    if (!("IntersectionObserver" in window)) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.unobserve(entry.target);
          }
        });
      },
      {
        rootMargin,
        threshold,
      }
    );

    const currentRef = imgRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, [src, rootMargin, threshold]);

  const handleLoad = (e) => {
    setIsLoaded(true);
    onLoad?.(e);
  };

  const handleError = (e) => {
    setHasError(true);
    setIsLoaded(true); // Show placeholder on error
    onError?.(e);
  };

  // Determine the image source
  const imageSrc = isInView && !hasError ? src : placeholder;
  const showPlaceholder = !isInView || hasError || !isLoaded;

  const containerClasses = [
    "lazy-image-container",
    isLoaded && fadeIn ? "lazy-image-loaded" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      ref={imgRef}
      className={containerClasses}
      style={{
        position: "relative",
        overflow: "hidden",
        ...style,
      }}
    >
      {/* Placeholder (shown until image loads) */}
      {showPlaceholder && (
        <img
          src={placeholder}
          alt=""
          className="lazy-image-placeholder"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            objectFit,
          }}
          aria-hidden="true"
        />
      )}

      {/* Actual image (only loaded when in view) */}
      {isInView && (
        <img
          src={imageSrc}
          alt={alt}
          className={`lazy-image ${isLoaded ? "lazy-image-visible" : "lazy-image-hidden"}`}
          style={{
            width: "100%",
            height: "100%",
            objectFit,
            opacity: isLoaded && !hasError ? 1 : 0,
            transition: fadeIn ? "opacity 0.3s ease-in-out" : "none",
          }}
          onLoad={handleLoad}
          onError={handleError}
          {...imgProps}
        />
      )}
    </div>
  );
};

/**
 * LazyBackgroundImage Component
 * For elements that need a background image
 */
export const LazyBackgroundImage = ({
  src,
  placeholder = DEFAULT_PLACEHOLDER,
  className = "",
  style = {},
  rootMargin = "50px",
  threshold = 0.1,
  children,
  ...divProps
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    setIsLoaded(false);
    setIsInView(false);
  }, [src]);

  useEffect(() => {
    if (!("IntersectionObserver" in window)) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin, threshold }
    );

    const currentRef = containerRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
      observer.disconnect();
    };
  }, [src, rootMargin, threshold]);

  useEffect(() => {
    if (isInView && src) {
      const img = new Image();
      img.src = src;
      img.onload = () => setIsLoaded(true);
    }
  }, [isInView, src]);

  const backgroundImage = isLoaded ? `url(${src})` : `url(${placeholder})`;

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        backgroundImage,
        backgroundSize: "cover",
        backgroundPosition: "center",
        transition: "background-image 0.3s ease-in-out",
        ...style,
      }}
      {...divProps}
    >
      {children}
    </div>
  );
};

/**
 * Hook for lazy loading images
 * Can be used in custom components
 */
export const useLazyImage = (src, options = {}) => {
  const { rootMargin = "50px", threshold = 0.1 } = options;
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [hasError, setHasError] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    setIsLoaded(false);
    setHasError(false);
    setIsInView(false);
  }, [src]);

  useEffect(() => {
    if (!("IntersectionObserver" in window)) {
      setIsInView(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsInView(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { rootMargin, threshold }
    );

    const currentRef = elementRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, [rootMargin, threshold]);

  useEffect(() => {
    if (isInView && src) {
      const img = new Image();
      img.src = src;
      img.onload = () => setIsLoaded(true);
      img.onerror = () => setHasError(true);
    }
  }, [isInView, src]);

  return {
    ref: elementRef,
    isLoaded,
    isInView,
    hasError,
    shouldLoad: isInView,
  };
};

export default LazyImage;
